"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDown, ArrowUp, Check, Minus } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";
import { AuthLink } from "@/components/shared/auth-link";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/components/card";
import { Input } from "@/lib/components/input";
import { Label } from "@/lib/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/components/select";
import { Slider } from "@/lib/components/slider";
import { Switch } from "@/lib/components/switch";
import { COPILOT_PLANS, DEFAULT_CACHE_HIT_RATE, DEVPASS_PRICE_RANGE, MODEL_MIXES, USAGE_PROFILES, copilotMonthlyCost, formatUsd, gatewayMonthlyCost, monthlyTokenUsage, rawUsageCost } from "./calc";
import type { Route } from "next";
export function CopilotCostCalculatorClient() {
  const $ = _c(213);
  const [developers, setDevelopers] = useState(5);
  const [planId, setPlanId] = useState("business");
  const [includedCredits, setIncludedCredits] = useState(0);
  const [profileId, setProfileId] = useState("moderate");
  const [chatSessionsPerDay, setChatSessionsPerDay] = useState(20);
  const [agentTasksPerDay, setAgentTasksPerDay] = useState(0);
  const [mixId, setMixId] = useState("premium");
  const [cacheHitRate, setCacheHitRate] = useState(DEFAULT_CACHE_HIT_RATE);
  const [byok, setByok] = useState(false);
  let t0;
  if ($[0] !== planId) {
    t0 = COPILOT_PLANS.find(p => p.id === planId) ?? COPILOT_PLANS[0];
    $[0] = planId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const plan = t0;
  let t1;
  if ($[2] !== mixId) {
    t1 = MODEL_MIXES.find(m => m.id === mixId) ?? MODEL_MIXES[0];
    $[2] = mixId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const mix = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = id => {
      setPlanId(id);
      const next = COPILOT_PLANS.find(p_0 => p_0.id === id);
      setIncludedCredits(next?.includedCredits ?? 0);
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const selectPlan = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = id_0 => {
      setProfileId(id_0);
      const profile = USAGE_PROFILES.find(p_1 => p_1.id === id_0);
      if (profile) {
        setChatSessionsPerDay(profile.chatSessionsPerDay);
        setAgentTasksPerDay(profile.agentTasksPerDay);
      }
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const selectProfile = t3;
  let copilot;
  let raw;
  let t4;
  let usage;
  if ($[6] !== agentTasksPerDay || $[7] !== byok || $[8] !== cacheHitRate || $[9] !== chatSessionsPerDay || $[10] !== developers || $[11] !== includedCredits || $[12] !== mix || $[13] !== plan.seatPrice) {
    usage = monthlyTokenUsage(developers, chatSessionsPerDay, agentTasksPerDay);
    raw = rawUsageCost(usage, mix);
    copilot = copilotMonthlyCost(developers, plan.seatPrice, includedCredits, raw.total);
    t4 = gatewayMonthlyCost(usage, mix, cacheHitRate, byok);
    $[6] = agentTasksPerDay;
    $[7] = byok;
    $[8] = cacheHitRate;
    $[9] = chatSessionsPerDay;
    $[10] = developers;
    $[11] = includedCredits;
    $[12] = mix;
    $[13] = plan.seatPrice;
    $[14] = copilot;
    $[15] = raw;
    $[16] = t4;
    $[17] = usage;
  } else {
    copilot = $[14];
    raw = $[15];
    t4 = $[16];
    usage = $[17];
  }
  const gateway = t4;
  const savings = copilot.total - gateway.total;
  const savingsPct = copilot.total > 0 ? savings / copilot.total : 0;
  let t5;
  if ($[18] !== copilot || $[19] !== gateway || $[20] !== raw || $[21] !== savings || $[22] !== savingsPct || $[23] !== usage) {
    t5 = {
      usage,
      raw,
      copilot,
      gateway,
      savings,
      savingsPct
    };
    $[18] = copilot;
    $[19] = gateway;
    $[20] = raw;
    $[21] = savings;
    $[22] = savingsPct;
    $[23] = usage;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const results = t5;
  const perDevGatewayUsage = developers > 0 ? results.gateway.total / developers : 0;
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mx-auto max-w-3xl text-center"><h1 className="text-4xl font-bold tracking-tight text-balance sm:text-5xl">GitHub Copilot Cost Calculator</h1><p className="mt-4 text-lg text-muted-foreground text-balance leading-relaxed">Copilot bills chat and agent usage in AI Credits since June 2026, with no default ceiling. Model your team's real usage and compare it with the same workload routed through LLM Gateway — same models, pass-through prices, prompt caching, and hard budget caps.</p></div>;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CardHeader><CardTitle>Your team's usage</CardTitle></CardHeader>;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Label htmlFor="developers">Developers</Label>;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e => {
      const parsed = parseInt(e.target.value, 10);
      if (!isNaN(parsed)) {
        setDevelopers(Math.min(1000, Math.max(1, parsed)));
      }
    };
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== developers) {
    t10 = <div className="flex items-center justify-between gap-4">{t8}<Input id="developers" type="number" min={1} max={1000} value={developers} onChange={t9} className="w-24 text-right" /></div>;
    $[29] = developers;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== developers) {
    t11 = [developers];
    $[31] = developers;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = t13 => {
      const [v] = t13;
      if (v !== undefined) {
        setDevelopers(v);
      }
    };
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t11) {
    t13 = <Slider value={t11} min={1} max={200} step={1} onValueChange={t12} />;
    $[34] = t11;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t10 || $[37] !== t13) {
    t14 = <div className="space-y-3">{t10}{t13}</div>;
    $[36] = t10;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Label>Copilot plan</Label>;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SelectContent>{COPILOT_PLANS.map(_temp)}</SelectContent>;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== planId) {
    t18 = <div className="space-y-2">{t15}<Select value={planId} onValueChange={selectPlan}>{t16}{t17}</Select></div>;
    $[42] = planId;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Label htmlFor="included-credits">Included AI Credits per seat ($/month)</Label>;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e_0 => {
      const parsed_0 = parseFloat(e_0.target.value);
      if (!isNaN(parsed_0)) {
        setIncludedCredits(Math.min(1000, Math.max(0, parsed_0)));
      }
    };
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== includedCredits) {
    t21 = <div className="flex items-center justify-between gap-4">{t19}<Input id="included-credits" type="number" min={0} max={1000} value={includedCredits} onChange={t20} className="w-24 text-right" /></div>;
    $[46] = includedCredits;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== plan.includedCredits) {
    t22 = plan.includedCredits === null && <p className="text-xs text-muted-foreground">Business and Enterprise credit allowances vary by agreement — set yours.</p>;
    $[48] = plan.includedCredits;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t21 || $[51] !== t22) {
    t23 = <div className="space-y-2">{t21}{t22}</div>;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Label>Usage profile</Label>;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SelectContent>{USAGE_PROFILES.map(_temp2)}</SelectContent>;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== profileId) {
    t27 = <div className="space-y-2">{t24}<Select value={profileId} onValueChange={selectProfile}>{t25}{t26}</Select></div>;
    $[56] = profileId;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Label>Chat sessions per developer per day</Label>;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== chatSessionsPerDay) {
    t29 = <div className="flex items-center justify-between gap-4">{t28}<span className="text-sm font-medium tabular-nums">{chatSessionsPerDay}</span></div>;
    $[59] = chatSessionsPerDay;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== chatSessionsPerDay) {
    t30 = [chatSessionsPerDay];
    $[61] = chatSessionsPerDay;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = t32 => {
      const [v_0] = t32;
      if (v_0 !== undefined) {
        setChatSessionsPerDay(v_0);
      }
    };
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t30) {
    t32 = <Slider value={t30} min={0} max={60} step={1} onValueChange={t31} />;
    $[64] = t30;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== t29 || $[67] !== t32) {
    t33 = <div className="space-y-3">{t29}{t32}</div>;
    $[66] = t29;
    $[67] = t32;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Label>Agent tasks per developer per day</Label>;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] !== agentTasksPerDay) {
    t35 = <div className="flex items-center justify-between gap-4">{t34}<span className="text-sm font-medium tabular-nums">{agentTasksPerDay}</span></div>;
    $[70] = agentTasksPerDay;
    $[71] = t35;
  } else {
    t35 = $[71];
  }
  let t36;
  if ($[72] !== agentTasksPerDay) {
    t36 = [agentTasksPerDay];
    $[72] = agentTasksPerDay;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = t38 => {
      const [v_1] = t38;
      if (v_1 !== undefined) {
        setAgentTasksPerDay(v_1);
      }
    };
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== t36) {
    t38 = <Slider value={t36} min={0} max={20} step={1} onValueChange={t37} />;
    $[75] = t36;
    $[76] = t38;
  } else {
    t38 = $[76];
  }
  let t39;
  if ($[77] !== t35 || $[78] !== t38) {
    t39 = <div className="space-y-3">{t35}{t38}</div>;
    $[77] = t35;
    $[78] = t38;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  let t40;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Label>Model mix</Label>;
    $[80] = t40;
  } else {
    t40 = $[80];
  }
  let t41;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>;
    $[81] = t41;
  } else {
    t41 = $[81];
  }
  let t42;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <SelectContent>{MODEL_MIXES.map(_temp3)}</SelectContent>;
    $[82] = t42;
  } else {
    t42 = $[82];
  }
  let t43;
  if ($[83] !== mixId) {
    t43 = <div className="space-y-2">{t40}<Select value={mixId} onValueChange={setMixId}>{t41}{t42}</Select></div>;
    $[83] = mixId;
    $[84] = t43;
  } else {
    t43 = $[84];
  }
  let t44;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Label>Prompt cache hit rate</Label>;
    $[85] = t44;
  } else {
    t44 = $[85];
  }
  let t45;
  if ($[86] !== cacheHitRate) {
    t45 = Math.round(cacheHitRate * 100);
    $[86] = cacheHitRate;
    $[87] = t45;
  } else {
    t45 = $[87];
  }
  let t46;
  if ($[88] !== t45) {
    t46 = <div className="flex items-center justify-between gap-4">{t44}<span className="text-sm font-medium tabular-nums">{t45}%</span></div>;
    $[88] = t45;
    $[89] = t46;
  } else {
    t46 = $[89];
  }
  let t47;
  if ($[90] !== cacheHitRate) {
    t47 = Math.round(cacheHitRate * 100);
    $[90] = cacheHitRate;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  let t48;
  if ($[92] !== t47) {
    t48 = [t47];
    $[92] = t47;
    $[93] = t48;
  } else {
    t48 = $[93];
  }
  let t49;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = t50 => {
      const [v_2] = t50;
      if (v_2 !== undefined) {
        setCacheHitRate(v_2 / 100);
      }
    };
    $[94] = t49;
  } else {
    t49 = $[94];
  }
  let t50;
  if ($[95] !== t48) {
    t50 = <Slider value={t48} min={0} max={80} step={5} onValueChange={t49} />;
    $[95] = t48;
    $[96] = t50;
  } else {
    t50 = $[96];
  }
  let t51;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <p className="text-xs text-muted-foreground">Coding tools resend system prompts and repo context on nearly every request; 60% is a conservative default for agentic workloads.</p>;
    $[97] = t51;
  } else {
    t51 = $[97];
  }
  let t52;
  if ($[98] !== t46 || $[99] !== t50) {
    t52 = <div className="space-y-3">{t46}{t50}{t51}</div>;
    $[98] = t46;
    $[99] = t50;
    $[100] = t52;
  } else {
    t52 = $[100];
  }
  let t53;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <Label htmlFor="byok">Bring your own provider keys (0% fee)</Label>;
    $[101] = t53;
  } else {
    t53 = $[101];
  }
  let t54;
  if ($[102] !== byok) {
    t54 = <div className="flex items-center justify-between gap-4">{t53}<Switch id="byok" checked={byok} onCheckedChange={setByok} /></div>;
    $[102] = byok;
    $[103] = t54;
  } else {
    t54 = $[103];
  }
  let t55;
  if ($[104] !== t14 || $[105] !== t18 || $[106] !== t23 || $[107] !== t27 || $[108] !== t33 || $[109] !== t39 || $[110] !== t43 || $[111] !== t52 || $[112] !== t54) {
    t55 = <Card className="lg:col-span-2">{t7}<CardContent className="space-y-6">{t14}{t18}{t23}{t27}{t33}{t39}{t43}{t52}{t54}</CardContent></Card>;
    $[104] = t14;
    $[105] = t18;
    $[106] = t23;
    $[107] = t27;
    $[108] = t33;
    $[109] = t39;
    $[110] = t43;
    $[111] = t52;
    $[112] = t54;
    $[113] = t55;
  } else {
    t55 = $[113];
  }
  let t56;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <CardHeader><CardTitle className="flex items-center justify-between">GitHub Copilot<span className="text-xs font-normal text-muted-foreground">estimated / month</span></CardTitle></CardHeader>;
    $[114] = t56;
  } else {
    t56 = $[114];
  }
  let t57;
  if ($[115] !== results.copilot.total) {
    t57 = formatUsd(results.copilot.total);
    $[115] = results.copilot.total;
    $[116] = t57;
  } else {
    t57 = $[116];
  }
  let t58;
  if ($[117] !== t57) {
    t58 = <p className="text-4xl font-bold tabular-nums">{t57}</p>;
    $[117] = t57;
    $[118] = t58;
  } else {
    t58 = $[118];
  }
  let t59;
  if ($[119] !== plan.seatPrice) {
    t59 = formatUsd(plan.seatPrice);
    $[119] = plan.seatPrice;
    $[120] = t59;
  } else {
    t59 = $[120];
  }
  let t60;
  if ($[121] !== developers || $[122] !== t59) {
    t60 = <dt className="text-muted-foreground">Seats ({developers} × {t59})</dt>;
    $[121] = developers;
    $[122] = t59;
    $[123] = t60;
  } else {
    t60 = $[123];
  }
  let t61;
  if ($[124] !== results.copilot.seatCost) {
    t61 = formatUsd(results.copilot.seatCost);
    $[124] = results.copilot.seatCost;
    $[125] = t61;
  } else {
    t61 = $[125];
  }
  let t62;
  if ($[126] !== t61) {
    t62 = <dd className="tabular-nums">{t61}</dd>;
    $[126] = t61;
    $[127] = t62;
  } else {
    t62 = $[127];
  }
  let t63;
  if ($[128] !== t60 || $[129] !== t62) {
    t63 = <div className="flex justify-between">{t60}{t62}</div>;
    $[128] = t60;
    $[129] = t62;
    $[130] = t63;
  } else {
    t63 = $[130];
  }
  let t64;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <dt className="text-muted-foreground">AI Credits usage</dt>;
    $[131] = t64;
  } else {
    t64 = $[131];
  }
  let t65;
  if ($[132] !== results.copilot.usageCost) {
    t65 = formatUsd(results.copilot.usageCost);
    $[132] = results.copilot.usageCost;
    $[133] = t65;
  } else {
    t65 = $[133];
  }
  let t66;
  if ($[134] !== t65) {
    t66 = <div className="flex justify-between">{t64}<dd className="tabular-nums">{t65}</dd></div>;
    $[134] = t65;
    $[135] = t66;
  } else {
    t66 = $[135];
  }
  let t67;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <dt className="text-muted-foreground">Included credits</dt>;
    $[136] = t67;
  } else {
    t67 = $[136];
  }
  let t68;
  if ($[137] !== results.copilot.includedCredits) {
    t68 = formatUsd(results.copilot.includedCredits);
    $[137] = results.copilot.includedCredits;
    $[138] = t68;
  } else {
    t68 = $[138];
  }
  let t69;
  if ($[139] !== t68) {
    t69 = <div className="flex justify-between">{t67}<dd className="tabular-nums">−{t68}</dd></div>;
    $[139] = t68;
    $[140] = t69;
  } else {
    t69 = $[140];
  }
  let t70;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <dt>Metered overage</dt>;
    $[141] = t70;
  } else {
    t70 = $[141];
  }
  let t71;
  if ($[142] !== results.copilot.overage) {
    t71 = formatUsd(results.copilot.overage);
    $[142] = results.copilot.overage;
    $[143] = t71;
  } else {
    t71 = $[143];
  }
  let t72;
  if ($[144] !== t71) {
    t72 = <div className="flex justify-between border-t border-border pt-2 font-medium">{t70}<dd className="tabular-nums">{t71}</dd></div>;
    $[144] = t71;
    $[145] = t72;
  } else {
    t72 = $[145];
  }
  let t73;
  if ($[146] !== t63 || $[147] !== t66 || $[148] !== t69 || $[149] !== t72) {
    t73 = <dl className="mt-4 space-y-2 text-sm">{t63}{t66}{t69}{t72}</dl>;
    $[146] = t63;
    $[147] = t66;
    $[148] = t69;
    $[149] = t72;
    $[150] = t73;
  } else {
    t73 = $[150];
  }
  let t74;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <p className="mt-4 text-xs text-muted-foreground">No spending ceiling by default — budgets are manual and off until configured.</p>;
    $[151] = t74;
  } else {
    t74 = $[151];
  }
  let t75;
  if ($[152] !== t58 || $[153] !== t73) {
    t75 = <Card>{t56}<CardContent>{t58}{t73}{t74}</CardContent></Card>;
    $[152] = t58;
    $[153] = t73;
    $[154] = t75;
  } else {
    t75 = $[154];
  }
  let t76;
  if ($[155] !== results.savingsPct) {
    t76 = <CardHeader><CardTitle className="flex items-center justify-between">Via LLM Gateway{results.savingsPct > 0.01 ? <Badge className="gap-1"><ArrowDown className="h-3 w-3" />{Math.round(results.savingsPct * 100)}% less</Badge> : results.savingsPct < -0.01 ? <Badge variant="outline" className="gap-1"><ArrowUp className="h-3 w-3" />{Math.round(-results.savingsPct * 100)}% more</Badge> : <Badge variant="outline" className="gap-1"><Minus className="h-3 w-3" />about the same</Badge>}</CardTitle></CardHeader>;
    $[155] = results.savingsPct;
    $[156] = t76;
  } else {
    t76 = $[156];
  }
  let t77;
  if ($[157] !== results.gateway.total) {
    t77 = formatUsd(results.gateway.total);
    $[157] = results.gateway.total;
    $[158] = t77;
  } else {
    t77 = $[158];
  }
  let t78;
  if ($[159] !== t77) {
    t78 = <p className="text-4xl font-bold tabular-nums text-primary">{t77}</p>;
    $[159] = t77;
    $[160] = t78;
  } else {
    t78 = $[160];
  }
  let t79;
  if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = <dt className="text-muted-foreground">Same workload, list rates</dt>;
    $[161] = t79;
  } else {
    t79 = $[161];
  }
  let t80;
  if ($[162] !== results.raw.total) {
    t80 = formatUsd(results.raw.total);
    $[162] = results.raw.total;
    $[163] = t80;
  } else {
    t80 = $[163];
  }
  let t81;
  if ($[164] !== t80) {
    t81 = <div className="flex justify-between">{t79}<dd className="tabular-nums">{t80}</dd></div>;
    $[164] = t80;
    $[165] = t81;
  } else {
    t81 = $[165];
  }
  let t82;
  if ($[166] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = <dt className="text-muted-foreground">After prompt caching</dt>;
    $[166] = t82;
  } else {
    t82 = $[166];
  }
  let t83;
  if ($[167] !== results.gateway.usageAfterCaching) {
    t83 = formatUsd(results.gateway.usageAfterCaching);
    $[167] = results.gateway.usageAfterCaching;
    $[168] = t83;
  } else {
    t83 = $[168];
  }
  let t84;
  if ($[169] !== t83) {
    t84 = <div className="flex justify-between">{t82}<dd className="tabular-nums">{t83}</dd></div>;
    $[169] = t83;
    $[170] = t84;
  } else {
    t84 = $[170];
  }
  const t85 = byok ? "0% BYOK" : "5% on credits";
  let t86;
  if ($[171] !== t85) {
    t86 = <dt className="text-muted-foreground">Platform fee ({t85})</dt>;
    $[171] = t85;
    $[172] = t86;
  } else {
    t86 = $[172];
  }
  let t87;
  if ($[173] !== results.gateway.fee) {
    t87 = formatUsd(results.gateway.fee);
    $[173] = results.gateway.fee;
    $[174] = t87;
  } else {
    t87 = $[174];
  }
  let t88;
  if ($[175] !== t87) {
    t88 = <dd className="tabular-nums">{t87}</dd>;
    $[175] = t87;
    $[176] = t88;
  } else {
    t88 = $[176];
  }
  let t89;
  if ($[177] !== t86 || $[178] !== t88) {
    t89 = <div className="flex justify-between">{t86}{t88}</div>;
    $[177] = t86;
    $[178] = t88;
    $[179] = t89;
  } else {
    t89 = $[179];
  }
  let t90;
  if ($[180] === Symbol.for("react.memo_cache_sentinel")) {
    t90 = <div className="flex justify-between border-t border-border pt-2 font-medium"><dt>Seat fees</dt><dd className="tabular-nums">$0</dd></div>;
    $[180] = t90;
  } else {
    t90 = $[180];
  }
  let t91;
  if ($[181] !== t81 || $[182] !== t84 || $[183] !== t89) {
    t91 = <dl className="mt-4 space-y-2 text-sm">{t81}{t84}{t89}{t90}</dl>;
    $[181] = t81;
    $[182] = t84;
    $[183] = t89;
    $[184] = t91;
  } else {
    t91 = $[184];
  }
  let t92;
  if ($[185] === Symbol.for("react.memo_cache_sentinel")) {
    t92 = <p className="mt-4 flex items-start gap-1.5 text-xs text-muted-foreground"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />Hard budget caps per org, project, and API key — spend stops at the limit.</p>;
    $[185] = t92;
  } else {
    t92 = $[185];
  }
  let t93;
  if ($[186] !== t78 || $[187] !== t91) {
    t93 = <CardContent>{t78}{t91}{t92}</CardContent>;
    $[186] = t78;
    $[187] = t91;
    $[188] = t93;
  } else {
    t93 = $[188];
  }
  let t94;
  if ($[189] !== t76 || $[190] !== t93) {
    t94 = <Card className="border-2 border-primary">{t76}{t93}</Card>;
    $[189] = t76;
    $[190] = t93;
    $[191] = t94;
  } else {
    t94 = $[191];
  }
  let t95;
  if ($[192] !== t75 || $[193] !== t94) {
    t95 = <div className="grid gap-6 sm:grid-cols-2">{t75}{t94}</div>;
    $[192] = t75;
    $[193] = t94;
    $[194] = t95;
  } else {
    t95 = $[194];
  }
  let t96;
  if ($[195] === Symbol.for("react.memo_cache_sentinel")) {
    t96 = <h3 className="font-semibold">Prefer a flat price per developer?</h3>;
    $[195] = t96;
  } else {
    t96 = $[195];
  }
  let t97;
  if ($[196] === Symbol.for("react.memo_cache_sentinel")) {
    t97 = formatUsd(DEVPASS_PRICE_RANGE.min);
    $[196] = t97;
  } else {
    t97 = $[196];
  }
  let t98;
  if ($[197] === Symbol.for("react.memo_cache_sentinel")) {
    t98 = formatUsd(DEVPASS_PRICE_RANGE.max);
    $[197] = t98;
  } else {
    t98 = $[197];
  }
  let t99;
  if ($[198] !== perDevGatewayUsage) {
    t99 = formatUsd(perDevGatewayUsage);
    $[198] = perDevGatewayUsage;
    $[199] = t99;
  } else {
    t99 = $[199];
  }
  let t100;
  if ($[200] !== t99) {
    t100 = <div>{t96}<p className="mt-1 text-sm text-muted-foreground">DevPass plans run {t97}–{t98} per developer per month across coding agents. Your current inputs work out to about{" "}{t99} per developer in gateway usage.</p></div>;
    $[200] = t99;
    $[201] = t100;
  } else {
    t100 = $[201];
  }
  let t101;
  if ($[202] === Symbol.for("react.memo_cache_sentinel")) {
    t101 = <Button asChild={true} variant="outline" className="shrink-0"><a href="https://devpass.llmgateway.io" target="_blank" rel="noopener noreferrer">See DevPass Plans</a></Button>;
    $[202] = t101;
  } else {
    t101 = $[202];
  }
  let t102;
  if ($[203] !== t100) {
    t102 = <Card><CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">{t100}{t101}</CardContent></Card>;
    $[203] = t100;
    $[204] = t102;
  } else {
    t102 = $[204];
  }
  let t103;
  if ($[205] === Symbol.for("react.memo_cache_sentinel")) {
    t103 = <Button asChild={true} size="lg"><AuthLink href="/signup">Start Free with LLM Gateway</AuthLink></Button>;
    $[205] = t103;
  } else {
    t103 = $[205];
  }
  let t104;
  if ($[206] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">{t103}<Button asChild={true} size="lg" variant="ghost"><Link href={"/compare/github-copilot" as Route}>See the Full Copilot Comparison</Link></Button></div>;
    $[206] = t104;
  } else {
    t104 = $[206];
  }
  let t105;
  if ($[207] !== t102 || $[208] !== t95) {
    t105 = <div className="space-y-6 lg:col-span-3">{t95}{t102}{t104}</div>;
    $[207] = t102;
    $[208] = t95;
    $[209] = t105;
  } else {
    t105 = $[209];
  }
  let t106;
  if ($[210] !== t105 || $[211] !== t55) {
    t106 = <section className="py-16 sm:py-24"><div className="container mx-auto px-4 sm:px-6 lg:px-8">{t6}<div className="mx-auto mt-12 grid max-w-6xl gap-6 lg:grid-cols-5">{t55}{t105}</div></div></section>;
    $[210] = t105;
    $[211] = t55;
    $[212] = t106;
  } else {
    t106 = $[212];
  }
  return t106;
}
function _temp3(m_0) {
  return <SelectItem key={m_0.id} value={m_0.id}>{m_0.label}</SelectItem>;
}
function _temp2(p_3) {
  return <SelectItem key={p_3.id} value={p_3.id}>{p_3.label}</SelectItem>;
}
function _temp(p_2) {
  return <SelectItem key={p_2.id} value={p_2.id}>{p_2.label}</SelectItem>;
}